<template>
  <WidgetChart
    v-if="!isLoading"
    title="Ranking de vendedores"
  >
    <template #default>
      <div
        v-if="error"
        class="text-red-600"
      >
        {{ error }}
      </div>
      <div v-else>
        <VueDataUi
          v-if="hasData"
          component="VueUiVerticalBar"
          :dataset="dataset"
          :config="config"
        />
        <div
          v-else
          class="text-sm text-gray-500"
        >
          No hay datos para el rango de fecha seleccionado.
        </div>
      </div>
    </template>
  </WidgetChart>
</template>

<script setup>
import {
  ref,
  watch,
  onMounted,
  computed,
} from 'vue';
import {
  VueDataUi,
  getVueDataUiConfig,
} from 'vue-data-ui';
import useApi from '@/shared/composables/useApi';
import WidgetChart from './base/chart.vue';
import { buildDateRangeQueryParams } from './utils';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const { isLoading, apiGet } = useApi();
const error = ref('');
const rawData = ref([]);
const dataset = ref([]);
const config = ref(getVueDataUiConfig('vue_ui_vertical_bar'));

config.value.legend = { show: false };
config.value.title = { show: true, text: 'Ranking de vendedores' };
config.value.axis = {
  x: {
    show: true,
  },
  y: {
    show: true,
    label: {
      formatter: ({ value }) => {
        const n = Number(value);
        if (isNaN(n)) return '';
        return n.toLocaleString(navigator.language, {
          style: 'currency',
          currency: 'ARS',
          minimumFractionDigits: 0,
        });
      },
    },
  },
};
config.value.tooltip = {
  show: true,
  formatter: ({ datapoint }) => {
    if (!datapoint) return '';
    const name = datapoint.name || '';
    const v = Number(datapoint.value);
    if (isNaN(v)) return name;
    return `${name}: ${v.toLocaleString(navigator.language, {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
    })}`;
  },
};

const hasData = computed(() => Array.isArray(dataset.value) && dataset.value.length > 0);

const normalize = (arr) =>
  arr
    .map((item) => ({
      name: (item.label || '').trim(),
      value: typeof item.value === 'number' ? item.value : parseFloat(item.value) || 0,
    }))
    .filter((it) => it.name)
    .sort((a, b) => b.value - a.value);

const fetchData = async () => {
  error.value = '';
  try {
    const params = buildDateRangeQueryParams(props.filters);
    const res = await apiGet('/dashboard/ranking-vendedores', { params });

    const normalized = normalize(Array.isArray(res.data) ? res.data : []);
    rawData.value = normalized;
    dataset.value = normalized.map((i) => ({
      name: i.name,
      value: i.value,
    }));
  } catch (e) {
    error.value = 'No se pudieron cargar los datos.';
    rawData.value = [];
    dataset.value = [];
  }
};

onMounted(fetchData);

watch(
  () => props.filters,
  () => {
    fetchData();
  },
  { deep: true },
);
</script>
